import { useEffect, useState } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Alert, Button, Dialog, Input, Select, Textarea } from "@/components/ui";
import type { BusinessTypeDto, VisibilityStatus } from "../types";
import { formatLabel } from "../labels";

const VISIBILITY: VisibilityStatus[] = ["visible", "hidden", "paused", "restricted"];

export interface CreateBusinessDialogProps {
  open: boolean;
  onClose: () => void;
  types: BusinessTypeDto[];
  onSubmit: (input: {
    business_type_id: string;
    market_id: string;
    legal_name: string;
    display_name: string;
    timezone_id: string;
    description?: string;
    owner_user_id?: string;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}

export function CreateBusinessDialog({
  open,
  onClose,
  types,
  onSubmit,
  busy = false,
  error = null,
}: CreateBusinessDialogProps) {
  const [businessTypeId, setBusinessTypeId] = useState(types[0]?.id ?? "");
  const [marketId, setMarketId] = useState("");
  const [legalName, setLegalName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [timezoneId, setTimezoneId] = useState("UTC");
  const [description, setDescription] = useState("");
  const [ownerUserId, setOwnerUserId] = useState("");

  useEffect(() => {
    if (open && !businessTypeId && types[0]) {
      setBusinessTypeId(types[0].id);
    }
  }, [open, types, businessTypeId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Create Business"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={
              !businessTypeId ||
              marketId.trim().length === 0 ||
              legalName.trim().length < 2 ||
              displayName.trim().length < 2 ||
              timezoneId.trim().length < 2
            }
            onClick={() =>
              void onSubmit({
                business_type_id: businessTypeId,
                market_id: marketId.trim(),
                legal_name: legalName.trim(),
                display_name: displayName.trim(),
                timezone_id: timezoneId.trim(),
                ...(description.trim() ? { description: description.trim() } : {}),
                ...(ownerUserId.trim() ? { owner_user_id: ownerUserId.trim() } : {}),
              })
            }
          >
            Create
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          A Business has exactly one Business Type. Marketplace Category is
          grouping, not behavior.
        </Text>
        <Select
          label="Business Type"
          value={businessTypeId}
          onChange={(event) => setBusinessTypeId(event.target.value)}
          options={types.map((type) => ({
            value: type.id,
            label: `${type.name} (${type.slug})`,
          }))}
        />
        <Input
          label="Market ID"
          value={marketId}
          onChange={(event) => setMarketId(event.target.value)}
        />
        <Input
          label="Legal name"
          value={legalName}
          onChange={(event) => setLegalName(event.target.value)}
        />
        <Input
          label="Display name"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
        />
        <Input
          label="Timezone (IANA)"
          value={timezoneId}
          onChange={(event) => setTimezoneId(event.target.value)}
        />
        <Textarea
          label="Description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        <Input
          label="Owner User ID (optional)"
          value={ownerUserId}
          onChange={(event) => setOwnerUserId(event.target.value)}
        />
        {error ? <Alert tone="danger" title="Create failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export interface ProfileEditDrawerProps {
  open: boolean;
  displayName: string;
  legalName: string;
  description: string;
  timezoneId: string;
  visibilityStatus: VisibilityStatus;
  onClose: () => void;
  onSubmit: (input: {
    legal_name: string;
    display_name: string;
    description: string | null;
    timezone_id: string;
    visibility_status: VisibilityStatus;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}

export function ProfileEditDrawer({
  open,
  displayName,
  legalName,
  description,
  timezoneId,
  visibilityStatus,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: ProfileEditDrawerProps) {
  const [legal, setLegal] = useState(legalName);
  const [display, setDisplay] = useState(displayName);
  const [desc, setDesc] = useState(description);
  const [tz, setTz] = useState(timezoneId);
  const [visibility, setVisibility] = useState(visibilityStatus);

  useEffect(() => {
    if (!open) return;
    setLegal(legalName);
    setDisplay(displayName);
    setDesc(description);
    setTz(timezoneId);
    setVisibility(visibilityStatus);
  }, [open, legalName, displayName, description, timezoneId, visibilityStatus]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Edit Business profile"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={() =>
              void onSubmit({
                legal_name: legal.trim(),
                display_name: display.trim(),
                description: desc.trim() || null,
                timezone_id: tz.trim(),
                visibility_status: visibility,
              })
            }
          >
            Save
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Input
          label="Legal name"
          value={legal}
          onChange={(event) => setLegal(event.target.value)}
        />
        <Input
          label="Display name"
          value={display}
          onChange={(event) => setDisplay(event.target.value)}
        />
        <Textarea
          label="Description"
          value={desc}
          onChange={(event) => setDesc(event.target.value)}
        />
        <Input
          label="Timezone (IANA)"
          value={tz}
          onChange={(event) => setTz(event.target.value)}
        />
        <Select
          label="Visibility"
          value={visibility}
          onChange={(event) =>
            setVisibility(event.target.value as VisibilityStatus)
          }
          options={VISIBILITY.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        {error ? <Alert tone="danger" title="Update failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}
